import { ChevronDown, ExternalLink } from 'lucide-react'
import type { ReleaseItem, UpdateKind } from '@manaro/contracts'
import { Badge, type BadgeTone } from '@/shared/ui/badge.tsx'
import { cn } from '@/shared/lib/cn.ts'
import { formatDate } from '@/shared/lib/format.ts'

const KIND_TONE: Record<UpdateKind, BadgeTone> = {
  Новое: 'brand',
  Улучшение: 'info',
  Исправление: 'warn',
}

interface ReleaseRowProps {
  item: ReleaseItem
  open: boolean
  onToggle: () => void
}

export function ReleaseRow({ item, open, onToggle }: ReleaseRowProps) {
  return (
    <div className="border-b border-line-soft last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-wrap items-start gap-x-4 gap-y-2.5 px-5 py-4.5 text-left transition-colors hover:bg-[#fcfbf7] md:flex-nowrap"
      >
        <div className="flex basis-full items-baseline gap-2.5 md:basis-24 md:shrink-0 md:flex-col md:gap-1">
          {item.version && <span className="tabular text-[13px] font-semibold text-brand">{item.version}</span>}
          <span className="text-[12.5px] whitespace-nowrap text-ink-muted">{formatDate(item.date)}</span>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[16px] font-medium tracking-[-0.005em]">{item.title}</span>
            <Badge tone={KIND_TONE[item.kind]}>{item.kind}</Badge>
            {item.areas.map((area) => (
              <Badge key={area} tone="outline" className="font-medium">
                {area}
              </Badge>
            ))}
          </div>
          {item.summary && <span className="text-[14px] text-ink-muted text-balance-pretty">{item.summary}</span>}
        </div>

        <ChevronDown
          className={cn('mt-0.5 size-4 shrink-0 text-ink-muted transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="px-4 pb-5 md:pr-5 md:pl-[132px]">
          <div className="rounded-control border border-line-soft bg-canvas px-4 py-3.5">
            {item.changes.length > 0 ? (
              <>
                <div className="mb-2.5 text-[12px] font-semibold tracking-[0.06em] text-ink-muted uppercase">
                  Что изменилось
                </div>
                <ul className="flex flex-col gap-1.5">
                  {item.changes.map((line) => (
                    <li key={line} className="flex gap-2.5 text-[14px] text-ink-soft text-balance-pretty">
                      <span className="mt-[7px] size-[5px] shrink-0 rounded-full bg-[#b9c7bc]" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-[14px] text-ink-muted text-balance-pretty">
                Подробного описания нет — в исходной задаче его не было.
              </p>
            )}

            <a
              href={item.source.url}
              target="_blank"
              rel="noreferrer"
              className="mt-3.5 inline-flex items-center gap-1.5 text-[12.5px] text-ink-muted hover:text-brand"
            >
              <ExternalLink className="size-3.5" />
              {item.source.repo} #{item.source.number}
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
